import { EX_DATATYPE } from "../constants/external-data";
import { validateEmail } from "../utils";
import Papa from 'papaparse';

function isExcelSerialDate(val) {
    return !isNaN(val) && Number(val) > 40000 && Number(val) < 90000;
}

function isDateTime(val) {
    if (!val) return false;
    const str = String(val).trim();
    if (/^(\d{1,2}:)?[0-5]?\d:[0-5]?\d(\.\d+)?$/.test(str)) {
        return true;
    }
    if (/^\d{1,10}$/.test(str)) return false;
    const parsed = Date.parse(str);
    if (!isNaN(parsed)) return true;
    if (/^\d{4}-\d{2}-\d{2}(T| )\d{2}:\d{2}:\d{2}/.test(str)) return true;
    if (/^\d{2}\/\d{2}\/\d{4}/.test(str)) return true;
    return false;
}

export function detectEmailType(sampleVals) {
    if (!Array.isArray(sampleVals) || sampleVals.length === 0) return null;

    const values = sampleVals
        .map(v => String(v ?? '').trim())
        .filter(v => v.length > 0);

    if (values.length === 0) return null;

    if (values.some(v => /[,;]/.test(v))) return null;

    const hasMultipleAt = (s) => (s.match(/@/g)?.length ?? 0) > 1;
    const isSingleEmail = (s) => !hasMultipleAt(s) && validateEmail(s);

    const domainRegex = /^(?!-)[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

    if (values.some(v => !v.includes('@') && domainRegex.test(v))) {
        return "domain";
    }

    if (values.some(v => v.includes('@') && isSingleEmail(v))) {
        return "email";
    }

    return null;
}

export function guessFieldType(values) {
    const NULLISH_TEXT = new Set(['null','nil','none','n/a','na','undefined']);
    const vals = values
        .filter(v => v !== undefined && v !== null)
        .map(v => String(v).trim())
        .filter(v => v !== '' && !NULLISH_TEXT.has(v.toLowerCase()));

    if (!vals.length) return EX_DATATYPE.STRING;

    const boolSet = new Set(['true', 'false', 'yes', 'no']);
    if (vals.every(v => boolSet.has(v.toLowerCase()))) return EX_DATATYPE.BOOLEAN;

    if (vals.every(v => !isNaN(v) && v !== '' && isFinite(Number(v)))) {
        if (vals.every(isExcelSerialDate)) return EX_DATATYPE.TIMESTAMP;
        return EX_DATATYPE.NUMERIC;
    }

    const percentPattern = /^-?\d+(\.\d+)?%$/;
    if (vals.some(v => percentPattern.test(v))) {
        return EX_DATATYPE.NUMERIC;
    }

    if (vals.every(v => isDateTime(v) || isExcelSerialDate(v))) return EX_DATATYPE.TIMESTAMP;

    return EX_DATATYPE.STRING;
}

export const cropCsvFile = (file, recordLimit = 100) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const text = e.target.result;
                const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(line => line.trim() !== '' && !/^,+$/.test(line));
                const firstLines = lines.slice(0, recordLimit + 1).join('\n');
                const blob = new Blob([firstLines], { type: 'text/csv' });
                const croppedFile = new File([blob], file.name.replace(/(\.csv)$/i, `_${recordLimit}rows.csv`), { type: 'text/csv' });
                resolve(croppedFile);
            } catch (err) { reject(err); }
        };
        reader.onerror = reject;
        reader.readAsText(file);
    });
};

export const getNewFields = (oldFields = [], newFields = []) => {
    const oldSet = new Set(oldFields.map(f => f.trim().toLowerCase()));
    return newFields.filter(f => !oldSet.has(f.trim().toLowerCase()));
};

export const validateFiles = (files) => {
    if (!files || files.length === 0) return 'Please upload at least one CSV file.';
    for (const f of files) {
        const name = f?.name?.toLowerCase?.() || '';
        if (!name.endsWith('.csv')) return 'Only .csv files are allowed.';
    }
    return undefined;
};

export function parseCsvFile(file, preview = 1001) {
    return new Promise((resolve, reject) => {
        Papa.parse(file, {
            skipEmptyLines: true,
            preview,
            worker: true,
            complete: (results) => resolve(results),
            error: (err) => reject(err),
        });
    });
}

export function mergeHeadersStable(headersList) {
    const seen = new Set();
    const merged = [];
    for (const headers of headersList) {
        for (const h of headers) {
            const key = (h ?? '').toString().trim();
            if (key && !seen.has(key.toLowerCase())) {
                seen.add(key.toLowerCase());
                merged.push(key);
            }
        }
    }
    return merged;
}

export function uniqueTopN(arr, n) {
    const seen = new Set();
    const res = [];
    for (const x of arr) {
        if (!seen.has(x)) {
            seen.add(x);
            res.push(x);
            if (res.length >= n) break;
        }
    }
    return res;
}

export const stripIsNewFields = (fieldsArr = []) =>
    fieldsArr.map(f => {
        const { isNew, ...rest } = f || {};
        return rest;
});

export const fileSig = (f) => `${f.name}|${f.size}|${f.lastModified ?? 0}`;

export const dedupFiles = (files) => {
    const seen = new Set();
    const out = [];
    for (const f of files) {
        const sig = fileSig(f);
        if (!seen.has(sig)) {
            seen.add(sig);
            out.push(f);
        }
    }
    return out;
};